<!--
  계약서 진행 흐름 — "지금 어디까지 왔고, 다음은 누가 무엇을 하나"를 화면에서 바로 보이게 한다.
  - mode="admin"    : 계약서 상세(인사담당 관점) — 준비 → 배포 → 직원 열람·동의 → 완료
  - mode="employee" : 내 계약서 상세(직원 관점)   — 열람 → 전체 확인 → 동의 → 완료
  - mode="overview" : 계약서 목록 상단 — 활성 단계 없이 전체 흐름·담당만 안내
  각 단계 아래에 담당(누가)을 적고, 현재 단계에 맞는 "다음 할 일" 문장을 한 줄로 보여준다.
-->
<template>
  <div class="flow">
    <el-steps :active="active" :process-status="processStatus" finish-status="success" align-center>
      <el-step v-for="s in steps" :key="s.title" :title="s.title" :description="s.who" :status="s.status" />
    </el-steps>
    <el-alert
      v-if="next"
      class="flow-next"
      :type="next.type"
      :closable="false"
      show-icon
    >
      <template #title>
        <strong>{{ next.who }}</strong> — {{ next.text }}
      </template>
    </el-alert>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  mode: { type: String, default: "admin" }, // admin | employee | overview
  // admin: 계약서 상세 객체(status, distribute_type, document_file, parties_summary, days_until_deadline, total_parties)
  // employee: 내 계약서 상세 객체(status, scroll_completed, require_full_read, minimum_read_seconds, can_consent)
  detail: { type: Object, default: null },
  // admin: 개별 배포에서 파일이 아직 없는 대상자 수
  missingFiles: { type: Number, default: 0 },
  // employee: 이번 세션까지 합산한 열람 시간(초)
  readSeconds: { type: Number, default: 0 },
});

const ADMIN_STEPS = [
  { title: "준비", who: "인사담당 · 양식·파일·대상자" },
  { title: "배포", who: "인사담당 · [배포하기] → 알림 발송" },
  { title: "열람·동의", who: "직원 · 내 계약서에서 확인" },
  { title: "완료", who: "시스템 자동 · 증빙 보관" },
];
const EMPLOYEE_STEPS = [
  { title: "열람", who: "문서를 끝까지 읽기" },
  { title: "전체 확인", who: "[전체 확인 완료] 누르기" },
  { title: "동의", who: "항목 체크 · 서명 → [동의합니다]" },
  { title: "완료", who: "완성본 PDF 내려받기 · 철회 불가" },
];
const OVERVIEW_STEPS = [
  { title: "양식", who: "인사담당 · 동의 규칙+빈 서식 (1회)" },
  { title: "계약서 생성", who: "인사담당 · 기한·대상자·문서 파일" },
  { title: "배포", who: "인사담당 · 직원에게 알림" },
  { title: "열람·동의", who: "직원 · 시스템이 독촉·만료 처리" },
  { title: "완료·증빙", who: "인사담당 · Excel 보관" },
];

const d = computed(() => props.detail || {});

const active = computed(() => {
  if (props.mode === "overview") return -1;
  if (props.mode === "employee") {
    const s = d.value.status;
    if (s === "CONSENTED") return 4;
    if (s === "REJECTED" || s === "EXPIRED") return 2;
    if (!d.value.scroll_completed && d.value.require_full_read) return d.value.read_count > 0 || props.readSeconds > 0 ? 1 : 0;
    return 2;
  }
  return { DRAFT: 0, DISTRIBUTED: 2, IN_PROGRESS: 2, COMPLETED: 4, EXPIRED: 2, CANCELLED: 0 }[d.value.status] ?? 0;
});

const isError = computed(() =>
  props.mode === "admin"
    ? ["CANCELLED", "EXPIRED"].includes(d.value.status)
    : ["REJECTED", "EXPIRED"].includes(d.value.status)
);
const processStatus = computed(() => (isError.value ? "error" : "process"));

const steps = computed(() => {
  const base = props.mode === "employee" ? EMPLOYEE_STEPS : props.mode === "overview" ? OVERVIEW_STEPS : ADMIN_STEPS;
  return base.map((s, i) => ({ ...s, status: isError.value && i === active.value ? "error" : undefined }));
});

// 현재 상태에서 "다음 할 일" — 담당 + 구체 행동 한 줄
const next = computed(() => {
  if (props.mode === "overview") return null;
  const s = d.value.status;

  if (props.mode === "employee") {
    if (s === "CONSENTED") return { who: "완료", type: "success", text: "동의가 기록되었습니다." + (d.value.has_signed_document ? " 아래에서 서명 완성본 PDF를 내려받아 보관하세요." : "") };
    if (s === "REJECTED") return { who: "완료", type: "warning", text: "거부가 기록되었습니다. 담당자가 확인 후 연락드립니다." };
    if (s === "EXPIRED") return { who: "기한 만료", type: "error", text: "확인 기한이 지나 동의할 수 없습니다. 인사담당에게 문의하세요." };
    const remain = Math.max(0, (d.value.minimum_read_seconds || 0) - props.readSeconds);
    if (d.value.require_full_read && !d.value.scroll_completed)
      return { who: "직원(본인)", type: "info", text: "문서를 끝까지 읽은 뒤 [전체 확인 완료]를 누르세요." + (remain > 0 ? ` (최소 열람 ${remain}초 남음)` : "") };
    if (remain > 0)
      return { who: "직원(본인)", type: "info", text: `최소 열람시간이 ${remain}초 남았습니다. 문서를 더 읽으면 동의 버튼이 열립니다.` };
    return { who: "직원(본인)", type: "warning", text: "아래 동의 항목을 체크하고" + (d.value.require_signature ? " 서명란에 서명한 뒤" : "") + " [동의합니다]를 누르세요. 동의 후에는 철회할 수 없습니다." };
  }

  const ps = d.value.parties_summary || {};
  const total = ps.total ?? d.value.total_parties ?? 0;
  const waiting = (ps.pending || 0) + (ps.read || 0);
  const dday = d.value.days_until_deadline;
  const ddayText = dday == null ? "" : dday < 0 ? ` (기한 ${-dday}일 지남)` : ` (D-${dday})`;

  if (s === "CANCELLED") return { who: "인사담당", type: "info", text: "취소된 건입니다. 필요하면 계약서를 새로 생성하세요." };
  if (s === "EXPIRED") return { who: "인사담당", type: "error", text: `기한이 만료되었습니다. 미동의 ${(ps.pending || 0) + (ps.read || 0) + (ps.expired || 0)}명은 새 계약서로 다시 배포해야 합니다.` };
  if (s === "COMPLETED") return { who: "인사담당", type: "success", text: "전원 동의 완료. [Excel 다운로드]로 열람·동의 기록을 증빙으로 보관하세요." };
  if (s === "DRAFT") {
    if (total === 0) return { who: "인사담당", type: "error", text: "대상자가 없습니다. 취소 후 대상(전직원/부서/개별)을 지정해 다시 생성하세요." };
    if (d.value.distribute_type === "COMMON" && !d.value.document_file)
      return { who: "인사담당", type: "error", text: "공통 계약서 파일이 없습니다. 취소 후 파일을 첨부해 다시 생성하세요." };
    if (d.value.distribute_type === "INDIVIDUAL" && props.missingFiles > 0)
      return d.value.template_has_body
        ? { who: "인사담당", type: "warning", text: d.value.template_uses_master
            ? `${props.missingFiles}명의 계약서가 아직 없습니다. 근로조건 관리에 조건이 없는 직원일 수 있습니다 — 등록 후 [근로조건 마스터에서 다시 가져오기]를 누르면 문서가 생성됩니다.`
            : (d.value.template_term_fields || []).length === 0
              ? `${props.missingFiles}명의 문서가 아직 없습니다. 대상자 목록의 [문서 다시 생성 (전원)]을 누르면 서식으로 PDF가 만들어집니다. 생성 후 [배포하기].`
              : `${props.missingFiles}명의 계약서가 아직 없습니다. "계약 조건"을 입력(또는 엑셀 올리기)하면 계약서 PDF가 자동 생성됩니다. 생성 후 [배포하기].` }
        : { who: "인사담당", type: "warning", text: `아래 대상자 목록에서 ${props.missingFiles}명의 개별 파일을 올린 뒤 [배포하기]를 누르세요.` };
    return { who: "인사담당", type: "success", text: `준비 완료. [배포하기]를 누르면 직원 ${total}명에게 알림이 갑니다. 배포 후에는 수정할 수 없습니다.` };
  }
  // DISTRIBUTED / IN_PROGRESS
  if ((ps.rejected || 0) > 0)
    return {
      who: "인사담당",
      type: "error",
      text: `${ps.rejected}명이 거부했습니다. 아래 대상자 목록에서 사유를 확인하고, 문서를 고쳐 [재요청]하거나 별도로 협의하세요.` +
        (waiting > 0 ? ` (그 외 ${waiting}명 대기 중${ddayText})` : ""),
    };
  return {
    who: "직원",
    type: waiting > 0 ? "warning" : "info",
    text: `${waiting}명이 열람·동의 대기 중${ddayText}. 동의 ${ps.consented || 0}/${total}명. 시스템이 기한 전 자동 독촉하며, 급하면 [독촉 발송]을 누르세요.`,
  };
});
</script>

<style scoped>
.flow { display: flex; flex-direction: column; gap: var(--sw-space-3); }
.flow-next :deep(.el-alert__title) { font-size: var(--sw-font-md); }
:deep(.el-step__description) { font-size: var(--sw-font-xs); }
</style>
